/**
 * 航班运营统计API
 * 提供BH/FH飞行小时统计，支持结算数据与MVT实时数据的混合查询
 */
import axiosInstance from "./axios";

/**
 * 获取飞行小时时间序列数据
 * @param {Object} params - 查询参数
 * @param {string} [params.aircraft_reg] - 飞机注册号，多个用逗号分隔
 * @param {string} [params.start_date] - 开始日期 (YYYY-MM-DD)
 * @param {string} [params.end_date] - 结束日期 (YYYY-MM-DD)，默认今天
 * @returns {Promise<{items: Array, total: number}>}
 */
export const getFlightHoursTimeSeries = async (params = {}) => {
    try {
        const response = await axiosInstance.get("/v1/flight-operation/flight-hours-time-series", {
            params,
        });
        return response.data;
    } catch (error) {
        console.error("获取飞行小时时间序列失败:", error);
        throw error;
    }
};

/**
 * 获取聚合飞行指标（月度+年度）
 * @param {Object} params - 查询参数
 * @param {string} params.aircraft_reg - 飞机注册号
 * @param {number} params.year - 年份
 * @param {number} params.month - 月份（1-12）
 * @returns {Promise<{aircraft_reg: string, monthly: Object, yearly: Object}>}
 */
export const getAggregatedMetrics = async (params) => {
    try {
        const response = await axiosInstance.get("/v1/flight-operation/aggregated-metrics", {
            params,
        });
        return response.data;
    } catch (error) {
        console.error("获取聚合飞行指标失败:", error);
        throw error;
    }
};

/**
 * 按日期范围获取聚合飞行指标（用于跨月场景）
 * @param {Object} params - 查询参数
 * @param {string} params.aircraft_reg - 飞机注册号
 * @param {string} params.start_date - 开始日期 (YYYY-MM-DD)
 * @param {string} params.end_date - 结束日期 (YYYY-MM-DD)
 * @returns {Promise<{aircraft_reg: string, monthly: Object, yearly: Object}>}
 */
export const getAggregatedMetricsRange = async (params) => {
    try {
        const response = await axiosInstance.get("/v1/flight-operation/aggregated-metrics-range", {
            params,
        });
        return response.data;
    } catch (error) {
        console.error("获取日期范围聚合飞行指标失败:", error);
        throw error;
    }
};

/**
 * 获取月度BH/FH时间序列（两层数据源）
 * @param {Object} params - 查询参数
 * @param {string} params.aircraft_reg - 飞机注册号，多个用逗号分隔
 * @param {number} [params.year] - 年份（与 start_date/end_date 二选一；整年 1-12 月）
 * @param {string} [params.start_date] - 开始日期 YYYY-MM-DD（与 end_date 成对；展开为覆盖整月）
 * @param {string} [params.end_date] - 结束日期 YYYY-MM-DD
 * @returns {Promise<Array<{aircraft_reg: string, year?: number, data: Array}>>}
 */
export const getMonthlyBhFhSeries = async (params) => {
    try {
        const response = await axiosInstance.get("/v1/flight-operation/bh-fh-monthly-series", {
            params,
        });
        return response.data;
    } catch (error) {
        console.error("获取月度BH/FH序列失败:", error);
        throw error;
    }
};

/**
 * 获取年度BH/FH时间序列（两层数据源）
 * @param {Object} params - 查询参数
 * @param {string} params.aircraft_reg - 飞机注册号，多个用逗号分隔
 * @returns {Promise<Array<{aircraft_reg: string, data: Array}>>}
 */
export const getYearlyBhFhSeries = async (params) => {
    try {
        const response = await axiosInstance.get("/v1/flight-operation/bh-fh-yearly-series", {
            params,
        });
        return response.data;
    } catch (error) {
        console.error("获取年度BH/FH序列失败:", error);
        throw error;
    }
};

/**
 * 按航线统计平均BH（两层数据源）
 * @param {Object} params - 查询参数
 * @param {string} params.aircraft_reg - 飞机注册号，多个用逗号分隔
 * @param {string} params.start_date - 开始日期 (YYYY-MM-DD)
 * @param {string} params.end_date - 结束日期 (YYYY-MM-DD)
 * @returns {Promise<Array>}
 */
export const getRouteAvgBh = async (params) => {
    try {
        const response = await axiosInstance.get("/v1/flight-operation/bh-fh-route-avg", { params });
        return response.data;
    } catch (error) {
        console.error("获取按航线统计平均BH失败:", error);
        throw error;
    }
};

/**
 * 按航线统计平均BH - 钻取明细（两层数据源）
 * @param {Object} params - 查询参数
 * @param {string} params.aircraft_reg - 飞机注册号（单个）
 * @param {string} params.route_code - 航线代码
 * @param {string} params.start_date - 开始日期 (YYYY-MM-DD)
 * @param {string} params.end_date - 结束日期 (YYYY-MM-DD)
 * @returns {Promise<Object>} 航线实例明细
 */
export const getRouteAvgBhDetail = async (params) => {
    try {
        const response = await axiosInstance.get("/v1/flight-operation/bh-fh-route-avg-detail", { params });
        return response.data;
    } catch (error) {
        console.error("获取航线平均BH钻取明细失败:", error);
        throw error;
    }
};

/**
 * 航段统计（两层数据源）
 * @param {Object} params - 查询参数
 * @param {string} params.aircraft_reg - 飞机注册号，多个用逗号分隔
 * @param {string} params.start_date - 开始日期 (YYYY-MM-DD)
 * @param {string} params.end_date - 结束日期 (YYYY-MM-DD)
 * @returns {Promise<Array>}
 */
export const getLegStats = async (params) => {
    try {
        const response = await axiosInstance.get("/v1/flight-operation/bh-fh-leg-stats", { params });
        return response.data;
    } catch (error) {
        console.error("获取航段统计失败:", error);
        throw error;
    }
};

/**
 * 获取滑行时间序列数据（按机场维度，数据源：MVT flight_operation）
 * @param {Object} params - 查询参数
 * @param {string} params.start_date - 开始日期 (YYYY-MM-DD)
 * @param {string} params.end_date - 结束日期 (YYYY-MM-DD)
 * @param {string} [params.aircraft_reg] - 飞机注册号过滤（可选），多个用逗号分隔
 * @param {string} [params.granularity] - 聚合粒度: day | week | month
 * @returns {Promise<Array<{airport: string, data: Object}>>}
 */
export const getTaxiTimeSeries = async (params) => {
    try {
        const response = await axiosInstance.get("/v1/flight-operation/taxi-time-series", { params });
        return response.data;
    } catch (error) {
        console.error("获取滑行时间序列失败:", error);
        throw error;
    }
};

const flightOperationStatsApi = {
    getFlightHoursTimeSeries,
    getAggregatedMetrics,
    getAggregatedMetricsRange,
    getMonthlyBhFhSeries,
    getYearlyBhFhSeries,
    getRouteAvgBh,
    getRouteAvgBhDetail,
    getLegStats,
    getTaxiTimeSeries,
};

export default flightOperationStatsApi;
